import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCheck, Trash2, X } from 'lucide-react';
import api from '../api/client';
import './Alerts.css';

interface Alert {
  id: string;
  device_id: string;
  device_name: string | null;
  alert_type: string;
  message: string;
  created_at: string;
  is_read: boolean;
}

const ALERT_EMOJI: Record<string, string> = {
  towing:           '🔴',
  geofence_exit:    '🟠',
  voltage_low:      '🟡',
  voltage_critical: '🔴',
  voltage_charging: '🟢',
};

export default function Alerts() {
  const { t } = useTranslation('alerts');
  const { t: tc } = useTranslation('common');
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const load = useCallback(() =>
    api.get('/alerts/').then((res) => setAlerts(res.data)).catch(() => {}), []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const markAll = async () => {
    setMarkingAll(true);
    try {
      await api.put('/alerts/read-all');
      setAlerts((prev) => prev.map((a) => ({ ...a, is_read: true })));
    } catch { /* ignore */ }
    setMarkingAll(false);
  };

  const markOne = async (id: string) => {
    try {
      await api.put(`/alerts/${id}/read`);
      setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, is_read: true } : a)));
    } catch { /* ignore */ }
  };

  const deleteOne = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.delete(`/alerts/${id}`);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch { /* ignore */ }
  };

  const deleteAllRead = async () => {
    try {
      await api.delete('/alerts/read');
      setAlerts((prev) => prev.filter((a) => !a.is_read));
    } catch { /* ignore */ }
  };

  if (loading) return <div className="alerts-empty"><div className="spinner" /></div>;

  const unreadCount = alerts.filter((a) => !a.is_read).length;
  const readCount = alerts.length - unreadCount;

  if (!alerts.length) {
    return (
      <div className="alerts-empty">
        <Bell size={40} strokeWidth={1.5} />
        <p>{t('settings.no_alerts')}</p>
      </div>
    );
  }

  return (
    <div className="alerts-page">
      <div className="alerts-header">
        <div className="alerts-header-left">
          <h2>{tc('nav.alerts')}</h2>
          {unreadCount > 0 && (
            <span className="alerts-badge">{unreadCount} {tc('errors.loading').includes('...') ? 'nowych' : 'new'}</span>
          )}
        </div>
        <div className="alerts-header-actions">
          {unreadCount > 0 && (
            <button className="alerts-mark-all" onClick={markAll} disabled={markingAll}>
              <CheckCheck size={16} />
              <span>{t('settings.mark_all_read')}</span>
            </button>
          )}
          {readCount > 0 && (
            <button className="alerts-mark-all" onClick={deleteAllRead}>
              <Trash2 size={16} />
              <span>{t('settings.delete_read')}</span>
            </button>
          )}
        </div>
      </div>

      <ul className="alerts-list">
        {alerts.map((a) => (
          <li
            key={a.id}
            className={`alert-item ${a.is_read ? 'read' : 'unread'}`}
            onClick={() => !a.is_read && markOne(a.id)}
          >
            <span className="alert-emoji" role="img" aria-label={a.alert_type}>
              {ALERT_EMOJI[a.alert_type] ?? '⚪'}
            </span>
            <div className="alert-content">
              <div className="alert-top">
                <span className="alert-type">{t(`types.${a.alert_type}`, { defaultValue: a.alert_type })}</span>
                {a.device_name && <span className="alert-device">{a.device_name}</span>}
              </div>
              <span className="alert-message">{a.message}</span>
              <span className="alert-time">{new Date(a.created_at).toLocaleString('pl-PL')}</span>
            </div>
            {!a.is_read && <div className="alert-dot" />}
            <button
              className="alert-delete"
              onClick={(e) => deleteOne(a.id, e)}
              aria-label={t('settings.delete_alert')}
            >
              <X size={16} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
